import { useState, useEffect, useRef, useCallback } from "react";
import { X, Loader2, Plus, Trash2, Sparkles, ImagePlus, UserRound, Shirt, ChevronLeft, Camera } from "lucide-react";
import { ImageGallery, type GalleryImage } from "./ImageGallery";
import { ProxyImage } from "./ProxyImage";

type Tab = "persons" | "garments";
type LLMModel = "gpt-5-mini" | "gpt-5.2";

const GARMENT_CATEGORIES = [
  { id: "replace_top", label: "Gornji dio", icon: "👕" },
  { id: "replace_bottom", label: "Donji dio", icon: "👖" },
  { id: "replace_outfit", label: "Outfit", icon: "👗" },
  { id: "replace_dress", label: "Haljina", icon: "👘" },
  { id: "add_accessory", label: "Dodatak", icon: "👜" },
  { id: "style_match", label: "Stil", icon: "🎨" },
] as const;

interface PersonSummary {
  id: number;
  name: string;
  analysisJson: string | null;
  primaryImageUrl: string | null;
  createdAt: string;
}

interface GarmentSummary {
  id: number;
  name: string;
  category: string;
  analysisJson: string | null;
  primaryImageUrl: string | null;
  createdAt: string;
}

interface RefImage {
  id: number;
  imageUrl: string;
  isPrimary: number;
}

interface PersonDetail {
  id: number;
  userId: number;
  name: string;
  analysisJson: string | null;
  analysisModel: string | null;
  analysisCost: number | null;
  images: RefImage[];
}

interface GarmentDetail {
  id: number;
  userId: number;
  name: string;
  category: string;
  analysisJson: string | null;
  analysisModel: string | null;
  analysisCost: number | null;
  images: RefImage[];
}

interface PersonGarmentLibraryProps {
  onClose: () => void;
  initialTab?: Tab;
  focusPersonId?: number | null;
  focusGarmentId?: number | null;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function PersonGarmentLibrary({ onClose, initialTab = "persons", focusPersonId, focusGarmentId }: PersonGarmentLibraryProps) {
  const [tab, setTab] = useState<Tab>(focusGarmentId ? "garments" : initialTab);
  const [persons, setPersons] = useState<PersonSummary[]>([]);
  const [garments, setGarments] = useState<GarmentSummary[]>([]);
  const [loading, setLoading] = useState(true);

  // Detail view
  const [selectedPersonId, setSelectedPersonId] = useState<number | null>(focusPersonId ?? null);
  const [selectedGarmentId, setSelectedGarmentId] = useState<number | null>(focusGarmentId ?? null);
  const [personDetail, setPersonDetail] = useState<PersonDetail | null>(null);
  const [garmentDetail, setGarmentDetail] = useState<GarmentDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Edit state
  const [editName, setEditName] = useState("");
  const [editCategory, setEditCategory] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeModel, setAnalyzeModel] = useState<LLMModel>("gpt-5-mini");
  const [uploading, setUploading] = useState(false);
  const [generatedImages, setGeneratedImages] = useState<GalleryImage[]>([]);

  // Create
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newCategory, setNewCategory] = useState("replace_top");
  const [showCreateForm, setShowCreateForm] = useState(false);

  // Delete confirm
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch lists
  const fetchPersons = useCallback(async () => {
    try {
      const res = await fetch("/api/persons");
      if (res.ok) setPersons(await res.json());
    } catch { /* ignore */ }
  }, []);

  const fetchGarments = useCallback(async () => {
    try {
      const res = await fetch("/api/garments");
      if (res.ok) setGarments(await res.json());
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    Promise.all([fetchPersons(), fetchGarments()]).finally(() => setLoading(false));
  }, [fetchPersons, fetchGarments]);

  // Fetch detail
  useEffect(() => {
    if (selectedPersonId) {
      setSelectedGarmentId(null);
      setGarmentDetail(null);
      setDetailLoading(true);
      Promise.all([
        fetch(`/api/persons/${selectedPersonId}`).then((r) => r.ok ? r.json() : null),
        fetch(`/api/persons/${selectedPersonId}/generated`).then((r) => r.ok ? r.json() : []),
      ]).then(([detail, generated]) => {
        if (detail) {
          setPersonDetail(detail);
          setEditName(detail.name);
        }
        setGeneratedImages(generated);
      }).finally(() => setDetailLoading(false));
    }
  }, [selectedPersonId]);

  useEffect(() => {
    if (selectedGarmentId) {
      setSelectedPersonId(null);
      setPersonDetail(null);
      setDetailLoading(true);
      Promise.all([
        fetch(`/api/garments/${selectedGarmentId}`).then((r) => r.ok ? r.json() : null),
        fetch(`/api/garments/${selectedGarmentId}/generated`).then((r) => r.ok ? r.json() : []),
      ]).then(([detail, generated]) => {
        if (detail) {
          setGarmentDetail(detail);
          setEditName(detail.name);
          setEditCategory(detail.category);
        }
        setGeneratedImages(generated);
      }).finally(() => setDetailLoading(false));
    }
  }, [selectedGarmentId]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const endpoint = tab === "persons" ? "/api/persons" : "/api/garments";
      const body = tab === "persons"
        ? { name: newName.trim() }
        : { name: newName.trim(), category: newCategory };
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        const data = await res.json();
        if (tab === "persons") {
          await fetchPersons();
          setSelectedPersonId(data.id);
        } else {
          await fetchGarments();
          setSelectedGarmentId(data.id);
        }
        setNewName("");
        setShowCreateForm(false);
      }
    } catch { /* ignore */ } finally {
      setCreating(false);
    }
  };

  const handleUpdateName = async () => {
    if (!editName.trim()) return;
    if (selectedPersonId && personDetail) {
      await fetch(`/api/persons/${selectedPersonId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: editName.trim() }),
      });
      setPersonDetail({ ...personDetail, name: editName.trim() });
      fetchPersons();
    } else if (selectedGarmentId && garmentDetail) {
      await fetch(`/api/garments/${selectedGarmentId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: editName.trim(), category: editCategory }),
      });
      setGarmentDetail({ ...garmentDetail, name: editName.trim(), category: editCategory });
      fetchGarments();
    }
  };

  const handleDelete = async (type: "person" | "garment", id: number) => {
    const endpoint = type === "person" ? `/api/persons/${id}` : `/api/garments/${id}`;
    await fetch(endpoint, { method: "DELETE" });
    if (type === "person") {
      setSelectedPersonId(null);
      setPersonDetail(null);
      fetchPersons();
    } else {
      setSelectedGarmentId(null);
      setGarmentDetail(null);
      fetchGarments();
    }
    setConfirmDelete(null);
  };

  const handleUploadImages = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const base64Images = await Promise.all(
        Array.from(files).filter((f) => f.type.startsWith("image/")).map(fileToBase64)
      );
      if (base64Images.length === 0) return;
      const id = selectedPersonId ?? selectedGarmentId;
      const type = selectedPersonId ? "persons" : "garments";
      const res = await fetch(`/api/${type}/${id}/images`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ images: base64Images }),
      });
      if (res.ok) {
        // Refresh detail
        if (selectedPersonId) {
          const detail = await fetch(`/api/persons/${selectedPersonId}`).then((r) => r.json());
          setPersonDetail(detail);
          fetchPersons();
        } else if (selectedGarmentId) {
          const detail = await fetch(`/api/garments/${selectedGarmentId}`).then((r) => r.json());
          setGarmentDetail(detail);
          fetchGarments();
        }
      }
    } catch { /* ignore */ } finally {
      setUploading(false);
    }
  };

  const handleDeleteImage = async (imgId: number) => {
    const id = selectedPersonId ?? selectedGarmentId;
    const type = selectedPersonId ? "persons" : "garments";
    await fetch(`/api/${type}/${id}/images/${imgId}`, { method: "DELETE" });
    if (selectedPersonId) {
      const detail = await fetch(`/api/persons/${selectedPersonId}`).then((r) => r.json());
      setPersonDetail(detail);
      fetchPersons();
    } else if (selectedGarmentId) {
      const detail = await fetch(`/api/garments/${selectedGarmentId}`).then((r) => r.json());
      setGarmentDetail(detail);
      fetchGarments();
    }
  };

  const handleAnalyze = async () => {
    const id = selectedPersonId ?? selectedGarmentId;
    const type = selectedPersonId ? "persons" : "garments";
    setAnalyzing(true);
    try {
      const res = await fetch(`/api/${type}/${id}/analyze`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ model: analyzeModel }),
      });
      if (res.ok) {
        const data = await res.json();
        if (selectedPersonId && personDetail) {
          setPersonDetail({ ...personDetail, analysisJson: JSON.stringify(data.analysis), analysisModel: analyzeModel, analysisCost: data.cost });
          fetchPersons();
        } else if (selectedGarmentId && garmentDetail) {
          setGarmentDetail({ ...garmentDetail, analysisJson: JSON.stringify(data.analysis), analysisModel: analyzeModel, analysisCost: data.cost });
          fetchGarments();
        }
      }
    } catch { /* ignore */ } finally {
      setAnalyzing(false);
    }
  };

  const goBack = () => {
    setSelectedPersonId(null);
    setSelectedGarmentId(null);
    setPersonDetail(null);
    setGarmentDetail(null);
    setGeneratedImages([]);
  };

  const inDetail = !!(selectedPersonId || selectedGarmentId);
  const detail = personDetail || garmentDetail;
  const detailImages = personDetail?.images ?? garmentDetail?.images ?? [];
  const analysis = detail?.analysisJson ? JSON.parse(detail.analysisJson) : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-6 backdrop-blur-sm"
      style={{ backgroundColor: "var(--effect-overlay-bg)" }}
      onClick={onClose}
    >
      <div
        className="bg-card rounded-2xl border border-border shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-border shrink-0">
          <div className="flex items-center gap-3">
            {inDetail && (
              <button onClick={goBack} className="text-muted-foreground hover:text-foreground transition-colors">
                <ChevronLeft className="w-4 h-4" />
              </button>
            )}
            <h2 className="text-sm font-semibold">
              {inDetail ? (editName || "Detalji") : "Biblioteka"}
            </h2>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Tab bar (list view only) */}
        {!inDetail && (
          <div className="flex border-b border-border shrink-0">
            <button
              onClick={() => setTab("persons")}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-[12px] font-medium transition-colors ${
                tab === "persons" ? "text-primary border-b-2 border-primary" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <UserRound className="w-3.5 h-3.5" />
              Osobe ({persons.length})
            </button>
            <button
              onClick={() => setTab("garments")}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-[12px] font-medium transition-colors ${
                tab === "garments" ? "text-primary border-b-2 border-primary" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Shirt className="w-3.5 h-3.5" />
              Odjevni predmeti ({garments.length})
            </button>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : inDetail ? (
            /* === DETAIL VIEW === */
            detailLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            ) : detail ? (
              <div className="space-y-5">
                {/* Name edit */}
                <div className="space-y-1.5">
                  <span className="text-[10px] text-muted-foreground/70">Naziv</span>
                  <input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onBlur={handleUpdateName}
                    onKeyDown={(e) => e.key === "Enter" && handleUpdateName()}
                    className="w-full px-2.5 py-1.5 text-sm bg-secondary/20 border border-border/60 rounded-lg outline-none focus:border-primary/40"
                  />
                </div>

                {/* Category selector (garments only) */}
                {selectedGarmentId && (
                  <div className="space-y-1.5">
                    <span className="text-[10px] text-muted-foreground/70">Vrsta</span>
                    <div className="grid grid-cols-3 gap-1">
                      {GARMENT_CATEGORIES.map((c) => (
                        <button
                          key={c.id}
                          onClick={() => { setEditCategory(c.id); }}
                          onMouseUp={() => {
                            // Save after state update
                            setTimeout(() => {
                              if (garmentDetail) {
                                fetch(`/api/garments/${selectedGarmentId}`, {
                                  method: "PUT",
                                  headers: { "Content-Type": "application/json" },
                                  body: JSON.stringify({ name: editName.trim(), category: c.id }),
                                });
                              }
                            }, 50);
                          }}
                          className={`flex items-center gap-1 px-1.5 py-1.5 text-[10px] font-medium rounded-md transition-all text-left ${
                            editCategory === c.id
                              ? "bg-primary/12 text-primary ring-1 ring-primary/30"
                              : "bg-secondary/20 text-muted-foreground hover:bg-secondary/40"
                          }`}
                        >
                          <span>{c.icon}</span>
                          <span className="truncate">{c.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Reference images */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-muted-foreground/70">Referentne slike ({detailImages.length})</span>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="flex items-center gap-1 text-[10px] text-primary hover:text-primary/80 transition-colors"
                    >
                      {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <ImagePlus className="w-3 h-3" />}
                      Dodaj
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(e) => { handleUploadImages(e.target.files); e.target.value = ""; }}
                      className="hidden"
                    />
                  </div>
                  {detailImages.length > 0 || uploading ? (
                    <div className="flex gap-2 flex-wrap">
                      {detailImages.map((img) => (
                        <div key={img.id} className="relative group w-28 h-28 rounded-lg overflow-hidden border border-border">
                          <ProxyImage src={{ type: tab === "persons" ? "person-image" : "garment-image", id: img.id }} className="w-full h-full object-cover" />
                          {img.isPrimary === 1 && (
                            <span className="absolute top-0.5 left-0.5 bg-primary text-primary-foreground text-[7px] px-1 py-0.5 rounded font-medium">
                              Glavna
                            </span>
                          )}
                          <button
                            onClick={() => handleDeleteImage(img.id)}
                            className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive/80"
                          >
                            <X className="w-2.5 h-2.5" />
                          </button>
                        </div>
                      ))}
                      {uploading && (
                        <div className="w-28 h-28 rounded-lg border border-dashed border-primary/30 flex flex-col items-center justify-center bg-primary/5">
                          <Loader2 className="w-4 h-4 animate-spin text-primary/60" />
                          <span className="text-[8px] text-primary/50 mt-0.5">Upload...</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className="border border-dashed border-border rounded-lg p-6 flex flex-col items-center justify-center cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-all"
                    >
                      <Camera className="w-6 h-6 text-muted-foreground/30 mb-1" />
                      <span className="text-[11px] text-muted-foreground/50">Dodajte referentne slike</span>
                    </div>
                  )}
                </div>

                {/* Analysis */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] text-muted-foreground/70">Analiza</span>
                      {analysis && (
                        <span className="text-[8px] bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 px-1.5 py-0.5 rounded-full font-medium">
                          Analizirano
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="flex rounded-md bg-secondary/40 p-0.5 gap-0.5">
                        {(["gpt-5-mini", "gpt-5.2"] as const).map((m) => (
                          <button
                            key={m}
                            onClick={() => setAnalyzeModel(m)}
                            className={`px-1.5 py-0.5 text-[9px] font-medium rounded transition-all ${
                              analyzeModel === m
                                ? "bg-card text-foreground shadow-sm"
                                : "text-muted-foreground hover:text-foreground"
                            }`}
                          >
                            {m === "gpt-5-mini" ? "Brzo" : "Premium"}
                          </button>
                        ))}
                      </div>
                      <button
                        onClick={handleAnalyze}
                        disabled={analyzing || detailImages.length === 0}
                        className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-30"
                      >
                        {analyzing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                        {analysis ? "Ponovo analiziraj" : "Analiziraj"}
                      </button>
                    </div>
                  </div>

                  {analysis && (
                    <div className="rounded-lg border border-border/60 bg-secondary/10 p-3 space-y-2">
                      {/* Person analysis */}
                      {analysis.description && (
                        <div>
                          <span className="text-[9px] text-muted-foreground/60 uppercase tracking-wider">Opis</span>
                          <p className="text-[11px] text-foreground/80 mt-0.5">{analysis.description}</p>
                        </div>
                      )}
                      {analysis.bodyType && (
                        <div>
                          <span className="text-[9px] text-muted-foreground/60 uppercase tracking-wider">Tip tijela</span>
                          <p className="text-[11px] text-foreground/80 mt-0.5">{analysis.bodyType}</p>
                        </div>
                      )}
                      {analysis.hairDescription && (
                        <div>
                          <span className="text-[9px] text-muted-foreground/60 uppercase tracking-wider">Kosa</span>
                          <p className="text-[11px] text-foreground/80 mt-0.5">{analysis.hairDescription}</p>
                        </div>
                      )}
                      {/* Garment analysis */}
                      {analysis.garmentTags && (
                        <div>
                          <span className="text-[9px] text-muted-foreground/60 uppercase tracking-wider">Oznake</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {analysis.garmentTags.map((tag: string, i: number) => (
                              <span key={i} className="text-[10px] bg-primary/10 text-primary px-1.5 py-0.5 rounded-md">
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {analysis.garmentDescription && (
                        <div>
                          <span className="text-[9px] text-muted-foreground/60 uppercase tracking-wider">Opis</span>
                          <p className="text-[11px] text-foreground/80 mt-0.5">{analysis.garmentDescription}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Generated images */}
                {generatedImages.length > 0 && (
                  <div className="space-y-2">
                    <span className="text-[10px] text-muted-foreground/70">Generirane slike ({generatedImages.length})</span>
                    <ImageGallery images={generatedImages} />
                  </div>
                )}

                {/* Delete */}
                <div className="pt-2 border-t border-border/40">
                  {confirmDelete ? (
                    <div className="flex items-center gap-2">
                      <span className="text-[11px] text-destructive">Jeste li sigurni?</span>
                      <button
                        onClick={() => handleDelete(selectedPersonId ? "person" : "garment", confirmDelete)}
                        className="px-2 py-1 text-[10px] font-medium rounded-md bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors"
                      >
                        Obriši
                      </button>
                      <button
                        onClick={() => setConfirmDelete(null)}
                        className="px-2 py-1 text-[10px] font-medium rounded-md border border-border text-muted-foreground hover:text-foreground transition-colors"
                      >
                        Odustani
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setConfirmDelete(selectedPersonId ?? selectedGarmentId ?? 0)}
                      className="flex items-center gap-1 text-[11px] text-destructive/70 hover:text-destructive transition-colors"
                    >
                      <Trash2 className="w-3 h-3" />
                      Obriši {selectedPersonId ? "osobu" : "odjevni predmet"}
                    </button>
                  )}
                </div>
              </div>
            ) : null
          ) : (
            /* === LIST VIEW === */
            <div className="space-y-3">
              {/* Create button / form */}
              {showCreateForm ? (
                <div className="rounded-lg border border-border/60 bg-secondary/10 p-3 space-y-2">
                  <input
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                    placeholder={tab === "persons" ? "Ime osobe..." : "Naziv odjevnog predmeta..."}
                    className="w-full px-2.5 py-1.5 text-sm bg-secondary/20 border border-border/60 rounded-lg outline-none focus:border-primary/40"
                    autoFocus
                  />
                  {tab === "garments" && (
                    <div className="grid grid-cols-3 gap-1">
                      {GARMENT_CATEGORIES.map((c) => (
                        <button
                          key={c.id}
                          onClick={() => setNewCategory(c.id)}
                          className={`flex items-center gap-1 px-1.5 py-1.5 text-[10px] font-medium rounded-md transition-all text-left ${
                            newCategory === c.id
                              ? "bg-primary/12 text-primary ring-1 ring-primary/30"
                              : "bg-secondary/20 text-muted-foreground hover:bg-secondary/40"
                          }`}
                        >
                          <span>{c.icon}</span>
                          <span className="truncate">{c.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-1.5">
                    <button
                      onClick={handleCreate}
                      disabled={creating || !newName.trim()}
                      className="flex-1 flex items-center justify-center gap-1 py-1.5 text-[11px] font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-30"
                    >
                      {creating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                      Dodaj
                    </button>
                    <button
                      onClick={() => { setShowCreateForm(false); setNewName(""); }}
                      className="px-3 py-1.5 text-[11px] font-medium rounded-md border border-border text-muted-foreground hover:text-foreground transition-colors"
                    >
                      Odustani
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setShowCreateForm(true)}
                  className="w-full flex items-center justify-center gap-1.5 py-2 text-[11px] font-medium rounded-lg border border-dashed border-primary/30 text-primary/70 hover:bg-primary/5 hover:border-primary/50 transition-all"
                >
                  <Plus className="w-3.5 h-3.5" />
                  {tab === "persons" ? "Nova osoba" : "Novi odjevni predmet"}
                </button>
              )}

              {/* Grid */}
              {tab === "persons" ? (
                persons.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground/40">
                    <UserRound className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    <p className="text-[11px]">Nema osoba</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    {persons.map((p) => (
                      <button
                        key={p.id}
                        onClick={() => setSelectedPersonId(p.id)}
                        className="flex flex-col items-center gap-2 p-3 rounded-xl border border-border hover:border-primary/40 hover:bg-primary/5 transition-all text-center"
                      >
                        <div className="w-16 h-16 rounded-full overflow-hidden bg-secondary/30 border-2 border-emerald-500/30">
                          {p.primaryImageUrl ? (
                            <ProxyImage src={{ type: "person", id: p.id }} alt={p.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <UserRound className="w-6 h-6 text-muted-foreground/30" />
                            </div>
                          )}
                        </div>
                        <div className="min-w-0 w-full">
                          <div className="text-[11px] font-medium truncate">{p.name}</div>
                          <div className="flex items-center justify-center gap-1 mt-0.5">
                            {p.analysisJson ? (
                              <span className="text-[8px] bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 px-1.5 py-0.5 rounded-full font-medium">
                                Analizirano
                              </span>
                            ) : (
                              <span className="text-[8px] text-muted-foreground/40">Bez analize</span>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )
              ) : garments.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground/40">
                  <Shirt className="w-8 h-8 mx-auto mb-2 opacity-30" />
                  <p className="text-[11px]">Nema odjevnih predmeta</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  {garments.map((g) => {
                    const cat = GARMENT_CATEGORIES.find((c) => c.id === g.category);
                    return (
                      <button
                        key={g.id}
                        onClick={() => setSelectedGarmentId(g.id)}
                        className="flex flex-col items-center gap-2 p-3 rounded-xl border border-border hover:border-primary/40 hover:bg-primary/5 transition-all text-center"
                      >
                        <div className="w-16 h-16 rounded-lg overflow-hidden bg-secondary/30 border-2 border-primary/30">
                          {g.primaryImageUrl ? (
                            <ProxyImage src={{ type: "garment", id: g.id }} alt={g.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Shirt className="w-6 h-6 text-muted-foreground/30" />
                            </div>
                          )}
                        </div>
                        <div className="min-w-0 w-full">
                          <div className="text-[11px] font-medium truncate">{g.name}</div>
                          <div className="flex items-center justify-center gap-1 mt-0.5">
                            {cat && <span className="text-[9px]">{cat.icon}</span>}
                            <span className="text-[9px] text-muted-foreground/50">{cat?.label}</span>
                          </div>
                          <div className="mt-0.5">
                            {g.analysisJson ? (
                              <span className="text-[8px] bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 px-1.5 py-0.5 rounded-full font-medium">
                                Analizirano
                              </span>
                            ) : (
                              <span className="text-[8px] text-muted-foreground/40">Bez analize</span>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
